import React, { useState } from "react";
import {
  FaReact, FaNodeJs, FaPython, FaJs, FaJava, FaDatabase,
  FaMobileAlt, FaServer, FaComments, FaLightbulb, FaBolt,
  FaPeopleArrows, FaGlobe, FaLeaf, FaLanguage,
} from "react-icons/fa";
import {
  SiAngular, SiSpringboot, SiPostgresql, SiMysql, SiMongodb,
  SiTensorflow, SiOpencv, SiFlutter, SiPandas,
  SiNumpy, SiTypescript, SiFirebase, SiExpress, SiNestjs,
  SiApachespark, SiGit, SiGoogleanalytics, SiJupyter,
} from "react-icons/si";
import "./Skills.css";

// ─── Skill categories ─────────────────────────────────────────────────────
const CATEGORIES = [
  {
    id: "languages",
    label: "Programming Languages",
    emoji: "{ }",
    skills: [
      { name: "Python",      icon: <FaPython />,     color: "#3776AB" },
      { name: "JavaScript",  icon: <FaJs />,         color: "#F7DF1E" },
      { name: "TypeScript",  icon: <SiTypescript />, color: "#3178C6" },
      { name: "Java",        icon: <FaJava />,       color: "#ED8B00" },
      { name: "SQL",         icon: <FaDatabase />,   color: "#4479A1" },
      { name: "C",           icon: <span>C</span>,   color: "#A8B9CC" },
      { name: "MATLAB",      icon: <span>M∗</span>,  color: "#E16737" },
    ],
  },
  {
    id: "web",
    label: "Web Frontend",
    emoji: "🌐",
    skills: [
      { name: "React.js",  icon: <FaReact />,   color: "#61DAFB" },
      { name: "Angular",   icon: <SiAngular />, color: "#DD0031" },
      { name: "HTML5",     icon: <span>H5</span>, color: "#E34F26" },
      { name: "CSS3",      icon: <span>C3</span>, color: "#1572B6" },
      { name: "Next.js",   icon: <span>N↗</span>, color: "#ffffff" },
      { name: "Tailwind",  icon: <span>TW</span>, color: "#06B6D4" },
    ],
  },
  {
    id: "mobile",
    label: "Mobile",
    emoji: "📱",
    skills: [
      { name: "React Native", icon: <FaMobileAlt />, color: "#61DAFB" },
      { name: "Flutter",      icon: <SiFlutter />,   color: "#02569B" },
    ],
  },
  {
    id: "backend",
    label: "Backend & APIs",
    emoji: "⚙️",
    skills: [
      { name: "Node.js",     icon: <FaNodeJs />,     color: "#339933" },
      { name: "Express.js",  icon: <SiExpress />,    color: "#fff" },
      { name: "NestJS",      icon: <SiNestjs />,     color: "#E0234E" },
      { name: "Spring Boot", icon: <SiSpringboot />, color: "#6DB33F" },
      { name: "Jakarta EE",  icon: <FaJava />,       color: "#E76F00" },
      { name: "REST APIs",   icon: <FaServer />,     color: "#ff69b4" },
      { name: "GraphQL",     icon: <span>GQL</span>, color: "#E10098" },
    ],
  },
  {
    id: "databases",
    label: "Databases",
    emoji: "🗄️",
    skills: [
      { name: "PostgreSQL", icon: <SiPostgresql />, color: "#4169E1" },
      { name: "MySQL",      icon: <SiMysql />,      color: "#4479A1" },
      { name: "MongoDB",    icon: <SiMongodb />,    color: "#47A248" },
      { name: "Firebase",   icon: <SiFirebase />,   color: "#FFCA28" },
    ],
  },
  {
    id: "mlai",
    label: "ML & AI",
    emoji: "🤖",
    skills: [
      { name: "TensorFlow",  icon: <SiTensorflow />,  color: "#FF6F00" },
      { name: "Scikit-Learn",icon: <span>SKL</span>, color: "#F89939" },
      { name: "OpenCV",      icon: <SiOpencv />,      color: "#5C3EE8" },
      { name: "Pandas",      icon: <SiPandas />,      color: "#150458" },
      { name: "NumPy",       icon: <SiNumpy />,       color: "#013243" },
      { name: "Matplotlib",  icon: <span style={{fontSize:"0.7rem",fontWeight:700}}>plt</span>, color: "#11557C" },
      { name: "Seaborn",     icon: <span>Sb</span>,   color: "#7BAFD4" },
      { name: "NLTK / NLP",  icon: <span>NLP</span>,  color: "#88c8ff" },
      { name: "YOLO",        icon: <span>YL</span>,   color: "#FF6B35" },
      { name: "K-Means / CAH", icon: <span>K⊕</span>, color: "#a855f7" },
      { name: "RFM Analysis",  icon: <span>RFM</span>, color: "#10b981" },
    ],
  },
  {
    id: "bigdata",
    label: "Big Data",
    emoji: "⚡",
    skills: [
      { name: "Apache Spark", icon: <SiApachespark />, color: "#E25A1C" },
      { name: "PySpark",      icon: <FaPython />,      color: "#E25A1C" },
      { name: "Jupyter",      icon: <SiJupyter />,     color: "#F37626" },
    ],
  },
  {
    id: "tools",
    label: "Tools & Platforms",
    emoji: "🛠️",
    skills: [
      { name: "Git",              icon: <SiGit />,            color: "#F05032" },
      { name: "Google Analytics",icon: <SiGoogleanalytics />, color: "#E37400" },
      { name: "Firebase",        icon: <SiFirebase />,        color: "#FFCA28" },
      { name: "Google Cloud",    icon: <FaServer />,          color: "#4285F4" },
      { name: "Postman",         icon: <span>PM</span>,       color: "#FF6C37" },
      { name: "Figma",           icon: <span>Fig</span>,      color: "#A259FF" },
      { name: "Excel / Power BI",icon: <span>XL</span>,       color: "#217346" },
      { name: "Linux",           icon: <span>🐧</span>,       color: "#FCC624" },
    ],
  },
];

const HUMAN_LANGUAGES = [
  { name: "Arabic",   level: "Native",              cefr: "C2", flag: "🇹🇳", dots: 6, color: "#FFB3D9" },
  { name: "English",  level: "Upper Intermediate",  cefr: "B2", flag: "🇬🇧", dots: 4, color: "#C8A8F0", cert: "IELTS 6.5" },
  { name: "French",   level: "Upper Intermediate",  cefr: "B2", flag: "🇫🇷", dots: 4, color: "#90D8D8" },
  { name: "Korean",   level: "Intermediate",        cefr: "B1", flag: "🇰🇷", dots: 3, color: "#E8D890" },
  { name: "Turkish",  level: "Beginner",            cefr: "A1", flag: "🇹🇷", dots: 1, color: "#E8906A" },
  { name: "Spanish",  level: "Beginner",            cefr: "A1", flag: "🇪🇸", dots: 1, color: "#78C8E8" },
];

const SOFT_SKILLS = [
  { name: "Cross-cultural Communication", icon: <FaGlobe />,       desc: "Lived and worked across TN · AT · NL" },
  { name: "Leadership",                   icon: <FaBolt />,        desc: "Co-founded startup, led platform dev" },
  { name: "Teamwork",                     icon: <FaPeopleArrows />, desc: "NGO, startup & corporate environments" },
  { name: "Public Speaking",              icon: <FaComments />,    desc: "Hult Prize competitor, event organiser" },
  { name: "Problem Solving",              icon: <FaLightbulb />,   desc: "9 ML models compared, 5 roles delivered" },
  { name: "Adaptability",                 icon: <FaLeaf />,        desc: "Tech → Communications in one semester" },
  { name: "Storytelling",                 icon: <FaLanguage />,    desc: "Digital content, science communication" },
];

// ─── SkillPill ─────────────────────────────────────────────────────────────
const SkillPill = ({ skill }) => (
  <div className="skill-pill" style={{ "--skill-color": skill.color }}>
    <span className="skill-pill__icon">{skill.icon}</span>
    <span className="skill-pill__name">{skill.name}</span>
  </div>
);

// ─── Skills ───────────────────────────────────────────────────────────────
const Skills = () => {
  const [activeTab, setActiveTab] = useState("technical");

  return (
    <div className="skills-section">
      <h2 className="section-title">Skills</h2>

      {/* Tab switcher */}
      <div className="skills-tabs">
        {[
          { id: "technical", label: "Technical" },
          { id: "languages", label: "Languages" },
          { id: "soft",      label: "Soft Skills" },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`skills-tab ${activeTab === tab.id ? "skills-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Technical skills */}
      {activeTab === "technical" && (
        <div className="skills-grid">
          {CATEGORIES.map((cat) => (
            <div key={cat.id} className="skill-cat">
              <div className="skill-cat__label">
                <span>{cat.emoji}</span> {cat.label}
              </div>
              <div className="skill-pills">
                {cat.skills.map((s) => (
                  <SkillPill key={s.name} skill={s} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Human languages */}
      {activeTab === "languages" && (
        <div className="lang-grid">
          {HUMAN_LANGUAGES.map((l) => (
            <div key={l.name} className="lang-card" style={{ "--lang-color": l.color }}>
              <span className="lang-card__flag">{l.flag}</span>
              <div className="lang-card__body">
                <div className="lang-card__name">{l.name}</div>
                <div className="lang-card__dots">
                  {[1,2,3,4,5,6].map((d) => (
                    <span key={d} className={`lang-dot ${d <= l.dots ? "lang-dot--on" : ""}`} />
                  ))}
                </div>
              </div>
              <div className="lang-card__right">
                <span className="lang-card__cefr">{l.cefr}</span>
                <span className="lang-card__level">{l.level}</span>
                {l.cert && <span className="lang-card__cert">{l.cert}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Soft skills */}
      {activeTab === "soft" && (
        <div className="soft-list">
          {SOFT_SKILLS.map((s) => (
            <div key={s.name} className="soft-item">
              <span className="soft-item__icon">{s.icon}</span>
              <div>
                <div className="soft-item__name">{s.name}</div>
                <div className="soft-item__desc">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Skills;
